import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Card, CardContent, Typography, TextField, Button, Alert, Grid } from "@mui/material";
import AdminLayout from "../AdminLayout";
import { useAuth } from "../../hooks/useAuth";
import ev from "../../services/eventV2Service";
import validators from "../../utils/validators";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

export default function EventRegisterV2() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [existingTeam, setExistingTeam] = useState([]);
  const [team, setTeam] = useState([{ name: "", phone: "", email: "" }]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const e = await ev.getEvent(eventId);
      setEvent(e);
      if (user?.uid) {
        const reg = await ev.getUserRegistration(eventId, user.uid);
        const existing = Array.isArray(reg?.team) ? reg.team : [];
        setExistingTeam(existing);
        setTeam(existing.length > 0 ? [] : [{ name: "", phone: "", email: "" }]);
      }
      setLoading(false);
    })();
  }, [eventId, user]);

  const addMember = () => {
    if ((existingTeam.length + team.length) >= 5) {
      setError("Maximum 5 team members allowed");
      return;
    }
    setTeam([...team, { name: "", phone: "", email: "" }]);
  };

  const removeMember = (idx) => {
    if (team.length <= 1 && existingTeam.length === 0) {
      setError("At least 1 team member required");
      return;
    }
    const copy = team.slice();
    copy.splice(idx, 1);
    setTeam(copy);
  };

  const updateMember = (idx, field, value) => {
    const copy = team.slice();
    copy[idx][field] = value;
    setTeam(copy);
  };

  const getNewMembers = () => {
    return team.filter((m) => m.name || m.phone || m.email);
  };

  const validateTeam = (newMembers) => {
    if ((existingTeam.length + newMembers.length) < 1) {
      setError("At least 1 team member required");
      return false;
    }
    if ((existingTeam.length + newMembers.length) > 5) {
      setError("Maximum 5 team members allowed");
      return false;
    }
    for (let i = 0; i < newMembers.length; i += 1) {
      const m = newMembers[i];
      if (!validators.isNotEmpty(m.name)) { setError(`New member ${i + 1}: name is required`); return false; }
      if (!validators.isValidTenDigitPhone(m.phone)) { setError(`New member ${i + 1}: enter 10-digit mobile number`); return false; }
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(m.email)) { setError(`New member ${i + 1}: enter valid email`); return false; }
    }
    setError("");
    return true;
  };

  const handleRegister = async () => {
    setError("");
    setSuccess("");
    try {
      // Pre-check quiz pass for external events to avoid permission-denied roundtrip
      if (event?.type === "external" && user?.uid) {
        const attemptId = `${eventId}_${user.uid}`;
        const snap = await getDoc(doc(db, "event_quiz_attempts", attemptId));
        const passed = snap.exists() ? !!snap.data().passed : false;
        const score = snap.exists() ? Number(snap.data().score || 0) : 0;
        const cutoff = Number(event.cutoff || 0);
        const qualified = passed || (score >= cutoff);
        if (!qualified) {
          setError(`Registration blocked: please pass the qualification quiz (cutoff ${cutoff}%). Your score: ${score}%.`);
          return;
        }
      }
      const newMembers = getNewMembers();
      if (!validateTeam(newMembers)) return;
      const mergedTeam = [...existingTeam, ...newMembers];
      await ev.registerForEventWithTeam(user.uid, eventId, mergedTeam);
      setSuccess("Registered successfully");
      navigate(`/student/events-v2/${eventId}`);
    } catch (e) {
      const msg = e.message || "Failed to register";
      if (/insufficient permissions|permission/i.test(msg)) {
        setError("Registration failed due to permissions. Please reattempt quiz once and try again.");
      } else {
        setError(msg);
      }
    }
  };

  return (
    <AdminLayout title="Team Registration">
      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Card sx={{ maxWidth: 720, mx: "auto", borderTop: "6px solid #003366" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>
              {event?.title || "Event"} — Team Registration
            </Typography>
            {existingTeam.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Already Added Members</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {existingTeam.map((m, idx) => (
                    <Alert key={`existing_${idx}`} severity="info">
                      {m.name} • {m.phone} • {m.email}
                    </Alert>
                  ))}
                </Box>
              </Box>
            )}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Add New Members
            </Typography>
            <Grid container spacing={2}>
              {team.map((m, idx) => (
                <React.Fragment key={idx}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Full Name" value={m.name} onChange={(e) => updateMember(idx, "name", e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Mobile (10 digits)" value={m.phone} onChange={(e) => updateMember(idx, "phone", e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Email" type="email" value={m.email} onChange={(e) => updateMember(idx, "email", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="outlined" onClick={() => removeMember(idx)}>Remove</Button>
                      {idx === team.length - 1 && <Button variant="contained" onClick={addMember}>Add Member</Button>}
                    </Box>
                  </Grid>
                </React.Fragment>
              ))}
              {team.length === 0 && (
                <Grid item xs={12}>
                  <Button variant="contained" onClick={addMember}>Add Member</Button>
                </Grid>
              )}
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" onClick={handleRegister}>Submit Registration</Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
